(() => {
  if (customElements.get('joffz-gallery-builder')) return;

  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const imageURL = value => {
    if (!value) return '';
    try {
      const url = new URL(typeof value === 'string' ? value : value.src || value.url, location.origin);
      return ['https:', 'http:'].includes(url.protocol) ? url.href : '';
    } catch { return ''; }
  };
  const imageFor = (product, variant) => imageURL(variant?.featured_image || product.featured_image || product.image || product.images?.[0]);
  const picture = (url, title, className = '') => {
    const image = element('img', className);
    image.src = url;
    image.alt = title;
    image.loading = 'lazy';
    image.decoding = 'async';
    return image;
  };

  class GalleryBuilder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      try {
        this.config = JSON.parse(this.querySelector('[data-gallery-config]').textContent);
        this.labels = this.config.labels;
        this.root = this.config.root.endsWith('/') ? this.config.root : `${this.config.root}/`;
        this.formatter = new Intl.NumberFormat(this.config.locale, { style: 'currency', currency: this.config.currency });
        this.catalog = new Map();
        this.selected = new Map();
        this.productCache = new Map();
        this.catalogCache = new Map();
        this.activeCollectionURL = this.config.collectionUrl || `${this.root}collections/all`;
        this.activeCollectionTitle = this.querySelector('[data-collection-select]')?.selectedOptions[0]?.textContent || '';
        this.catalogSequence = 0;
        this.modalSequence = 0;
        this.listeners = new AbortController();
        this.dialog = this.querySelector('[data-product-dialog]');
        this.setDialog = this.querySelector('[data-set-dialog]');
        this.setPanel = this.querySelector('[data-set-panel]');
        this.setAnchor = this.querySelector('[data-set-anchor]');
        this.mobileMedia = matchMedia('(max-width: 849px)');
        this.grid = this.querySelector('[data-products]');
        this.loadMoreButton = this.querySelector('[data-load-more]');
        this.addButton = this.querySelector('[data-add-set]');
        this.on('click', event => this.onClick(event));
        this.on('submit', event => {
          if (event.target.matches('[data-search-form]')) {
            event.preventDefault();
            clearTimeout(this.searchTimer);
            this.searchCatalog();
          }
          if (event.target.matches('[data-product-form]')) {
            event.preventDefault();
            this.confirmSelection();
          }
        });
        this.on('input', event => {
          if (!event.target.matches('[data-search-input]') || this.busy) return;
          clearTimeout(this.searchTimer);
          this.searchTimer = setTimeout(() => this.searchCatalog(), 280);
        });
        this.on('change', event => {
          if (event.target.matches('[data-option-index]')) this.changeOption(Number(event.target.dataset.optionIndex), event.target.value);
          if (event.target.matches('[data-collection-select]') && !this.busy) this.chooseCollection(event.target.value);
        });
        this.on('keydown', event => {
          const choice = event.target.closest('[data-finish-value]');
          if (!choice || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const choices = [...choice.parentElement.querySelectorAll('[data-finish-value]:not(:disabled)')];
          const direction = ['ArrowLeft', 'ArrowUp'].includes(event.key) ? -1 : 1;
          const index = event.key === 'Home' ? 0 : event.key === 'End' ? choices.length - 1 : (choices.indexOf(choice) + direction + choices.length) % choices.length;
          choices[index]?.click();
          choices[index]?.focus();
        });
        this.dialog.addEventListener('close', () => this.onDialogClose(), { signal: this.listeners.signal });
        this.dialog.addEventListener('click', event => {
          if (event.target !== this.dialog) return;
          const rect = this.dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
        }, { signal: this.listeners.signal });
        this.setDialog.addEventListener('close', () => this.onSetClose(), { signal: this.listeners.signal });
        this.setDialog.addEventListener('click', event => {
          if (event.target !== this.setDialog) return;
          const rect = this.setDialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.setDialog.close();
        }, { signal: this.listeners.signal });
        this.mobileMedia.addEventListener('change', () => this.placeSetPanel(), { signal: this.listeners.signal });
        this.placeSetPanel();
        this.acceptCatalog(JSON.parse(this.querySelector('[data-gallery-catalog]').textContent), false);
        this.renderSummary();
      } catch {
        const status = this.querySelector('[data-catalog-status]');
        if (status) status.textContent = this.labels?.catalog_error || 'The prints could not load. Please refresh the page.';
      }
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.catalogController?.abort();
      this.modalController?.abort();
      clearTimeout(this.searchTimer);
      if (this.dialog?.open) this.dialog.close();
      if (this.setDialog?.open) this.setDialog.close();
      if (this.savedOverflow !== undefined) document.documentElement.style.overflow = this.savedOverflow;
      if (this.setSavedOverflow !== undefined) document.documentElement.style.overflow = this.setSavedOverflow;
      this.initialized = false;
    }

    on(type, handler) { this.addEventListener(type, handler, { signal: this.listeners.signal }); }
    money(cents) { return this.formatter.format(Number(cents) / 100); }
    label(key, values = {}) { return Object.entries(values).reduce((text, [name, value]) => text.replaceAll(`[${name}]`, String(value)), this.labels[key] || ''); }
    selectionLabel(count) {
      const label = count === 1 && /^en(?:-|$)/i.test(this.config.locale || 'en') ? this.labels.print_count.replace(/\bprints\b/i, 'print') : this.labels.print_count;
      return `${count} ${label}`;
    }
    placeSetPanel() {
      if (this.mobileMedia.matches) this.setDialog.append(this.setPanel);
      else {
        if (this.setDialog.open) this.setDialog.close();
        this.setAnchor.append(this.setPanel);
      }
    }
    openSet() {
      if (!this.mobileMedia.matches || this.setDialog.open) return;
      this.setSavedOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.setDialog.showModal();
      this.querySelector('[data-set-heading]').focus({ preventScroll: true });
    }
    onSetClose() {
      if (this.setSavedOverflow !== undefined) {
        document.documentElement.style.overflow = this.setSavedOverflow;
        this.setSavedOverflow = undefined;
      }
      if (this.mobileMedia.matches) this.querySelector('[data-show-selection]').focus({ preventScroll: true });
      this.setStep(1);
    }
    setStep(step) {
      this.querySelectorAll('[data-builder-step]').forEach(button => {
        const current = Number(button.dataset.builderStep) === step;
        button.parentElement.classList.toggle('is-current', current);
        if (current) button.setAttribute('aria-current', 'step');
        else button.removeAttribute('aria-current');
      });
    }
    localURL(raw) {
      const url = new URL(raw, location.origin);
      if (url.origin !== location.origin || !['https:', 'http:'].includes(url.protocol)) throw new Error('Invalid store URL');
      return url;
    }
    productURL(handle, json = false) { return this.localURL(`${this.root}products/${encodeURIComponent(handle)}${json ? '.js' : ''}`); }

    async read(url, format = 'json', signal) {
      const controller = new AbortController();
      const abort = () => controller.abort();
      if (signal?.aborted) controller.abort();
      else signal?.addEventListener('abort', abort, { once: true });
      const timeout = setTimeout(abort, 15000);
      try {
        const response = await fetch(url, {
          credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
          headers: { Accept: format === 'json' ? 'application/json' : 'text/html' }
        });
        if (!response.ok) throw new Error(`Store request failed: ${response.status}`);
        return format === 'json' ? await response.json() : await response.text();
      } finally {
        clearTimeout(timeout);
        signal?.removeEventListener('abort', abort);
      }
    }

    onClick(event) {
      const step = event.target.closest('[data-builder-step]');
      if (step && !this.busy) {
        if (step.dataset.builderStep === '1') {
          this.setStep(1);
          this.querySelector('[data-catalog-heading]').scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
          this.querySelector('[data-catalog-heading]').focus({ preventScroll: true });
        } else if (step.dataset.builderStep === '2') {
          const selectedId = this.selected.keys().next().value;
          const id = selectedId || this.catalog.keys().next().value;
          if (id) this.openProduct(id, selectedId ? 'selection' : 'catalog');
        } else this.reviewSet();
      }
      const choose = event.target.closest('[data-choose]');
      if (choose && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
        event.preventDefault();
        if (!this.busy) this.openProduct(choose.dataset.choose, 'catalog');
      }
      const edit = event.target.closest('[data-edit]');
      if (edit && !this.busy) this.openProduct(edit.dataset.edit, 'selection');
      const collection = event.target.closest('[data-collection-url]');
      if (collection && !this.busy) this.chooseCollection(collection.dataset.collectionUrl);
      const remove = event.target.closest('[data-remove]');
      if (remove && !this.busy) {
        this.selected.delete(remove.dataset.remove);
        this.renderSummary();
        this.updateCatalogSelection();
        this.setMessage(this.labels.selection_removed);
        this.querySelector(this.setDialog.open ? '[data-set-heading]' : '[data-catalog-heading]').focus({ preventScroll: true });
      }
      if (event.target.closest('[data-dialog-close]')) this.dialog.close();
      if (event.target.closest('[data-set-close]')) this.setDialog.close();
      const finish = event.target.closest('[data-finish-value]');
      if (finish && !finish.disabled) this.changeOption(Number(finish.dataset.finishIndex), finish.dataset.finishValue);
      if (event.target.closest('[data-load-more]') && !this.busy && this.nextPage && !this.loadingCatalog) this.loadCatalog(this.nextPage, true);
      if (event.target.closest('[data-add-set]')) this.addSet();
      if (event.target.closest('[data-show-selection]')) {
        this.reviewSet();
      }
    }

    reviewSet() {
      this.setStep(3);
      if (this.mobileMedia.matches) this.openSet();
      else {
        this.setPanel.scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        this.querySelector('[data-set-heading]').focus({ preventScroll: true });
      }
    }

    searchCatalog() {
      if (this.busy) return;
      const query = this.querySelector('[data-search-input]').value.trim();
      const url = this.localURL(query ? this.config.searchUrl : this.activeCollectionURL);
      if (query) {
        url.searchParams.set('q', query);
        url.searchParams.set('type', 'product');
        url.searchParams.set('options[prefix]', 'last');
      }
      this.loadCatalog(url, false);
    }

    chooseCollection(rawURL) {
      if (this.busy) return;
      clearTimeout(this.searchTimer);
      this.querySelector('[data-search-input]').value = '';
      this.setStep(1);
      this.loadCatalog(this.localURL(rawURL), false);
    }

    updateCollectionChoice(url) {
      const searching = url.pathname === this.localURL(this.config.searchUrl).pathname;
      if (!searching) {
        this.activeCollectionURL = url.pathname;
        const selector = this.querySelector('[data-collection-select]');
        selector.value = url.pathname;
        this.activeCollectionTitle = selector.selectedOptions[0]?.textContent || '';
      }
      this.querySelectorAll('[data-collection-url]').forEach(button => {
        button.setAttribute('aria-pressed', String(!searching && this.localURL(button.dataset.collectionUrl).pathname === url.pathname));
      });
      const status = this.querySelector('[data-catalog-status]');
      if (this.catalog.size && !searching && this.activeCollectionTitle) status.textContent = `${this.activeCollectionTitle} · ${this.catalog.size}`;
    }

    async loadCatalog(rawURL, append) {
      const sequence = ++this.catalogSequence;
      this.catalogController?.abort();
      this.catalogController = new AbortController();
      this.loadingCatalog = true;
      this.loadMoreButton.disabled = true;
      this.grid.setAttribute('aria-busy', 'true');
      this.querySelector('[data-catalog-status]').textContent = this.labels.loading;
      try {
        const url = this.localURL(rawURL);
        if (/\/products\/[^/]+\/?$/.test(url.pathname)) {
          url.pathname = this.localURL(this.activeCollectionURL || this.config.collectionUrl || `${this.root}collections/all`).pathname;
        }
        url.searchParams.set('section_id', 'joffz-gallery-builder');
        const theme = window.Shopify?.theme;
        const preview = new URLSearchParams(location.search).get('preview_theme_id') || (['unpublished', 'development'].includes(theme?.role) ? String(theme.id) : '');
        if (preview && /^\d+$/.test(preview)) url.searchParams.set('preview_theme_id', preview);
        const key = url.href;
        const cached = this.catalogCache.get(key);
        let data;
        if (cached && Date.now() - cached.time < 60000) data = cached.data;
        else {
          const html = await this.read(url, 'text', this.catalogController.signal);
          if (sequence !== this.catalogSequence) return;
          const source = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-gallery-catalog]');
          if (!source) throw new Error('Missing catalogue data');
          data = JSON.parse(source.textContent);
          if (!Array.isArray(data.products)) throw new Error('Invalid catalogue');
          this.catalogCache.set(key, { data, time: Date.now() });
          if (this.catalogCache.size > 8) this.catalogCache.delete(this.catalogCache.keys().next().value);
        }
        if (sequence !== this.catalogSequence) return;
        this.acceptCatalog(data, append);
        this.updateCollectionChoice(url);
      } catch {
        if (sequence !== this.catalogSequence || this.catalogController.signal.aborted) return;
        this.querySelector('[data-catalog-status]').textContent = this.labels.catalog_error;
        this.querySelector('[data-collection-select]').value = this.activeCollectionURL;
      } finally {
        if (sequence === this.catalogSequence) {
          this.loadingCatalog = false;
          this.grid.removeAttribute('aria-busy');
          this.loadMoreButton.disabled = false;
        }
      }
    }

    acceptCatalog(data, append) {
      if (!Array.isArray(data.products)) throw new Error('Invalid catalogue');
      if (!append) this.catalog.clear();
      data.products.forEach(product => {
        if (!product.id || !product.handle || Number(product.id) === this.config.placeholderProductId || product.handle === 'create-your-own-gallery-wall') return;
        this.catalog.set(String(product.id), product);
      });
      this.nextPage = data.next ? this.localURL(data.next).href : null;
      this.loadMoreButton.hidden = !this.nextPage;
      this.grid.replaceChildren();
      this.catalog.forEach(product => this.grid.append(this.renderCard(product)));
      this.querySelector('[data-catalog-status]').textContent = this.catalog.size ? this.labels.search_results : this.labels.no_results;
      this.updateCatalogSelection();
    }

    renderCard(product) {
      const card = element('article', 'jgb-product');
      card.dataset.productId = String(product.id);
      const link = element('a', 'jgb-product-image');
      link.href = this.productURL(product.handle).href;
      link.dataset.choose = String(product.id);
      link.setAttribute('aria-label', `${this.labels.choose}: ${product.title}`);
      const image = imageURL(product.image);
      if (image) link.append(picture(image, product.title));
      card.append(link, element('h3', '', product.title), element('span', 'jgb-product-price', `${this.labels.from} ${this.money(product.price)}`));
      const button = element('button', '', this.labels.choose);
      button.type = 'button';
      button.dataset.choose = String(product.id);
      button.setAttribute('aria-haspopup', 'dialog');
      button.setAttribute('aria-label', `${this.labels.choose}: ${product.title}`);
      card.append(button);
      return card;
    }

    updateCatalogSelection() {
      this.grid.querySelectorAll('[data-product-id]').forEach(card => {
        const selected = this.selected.has(card.dataset.productId);
        const existing = card.querySelector('.jgb-selected-badge');
        if (selected && !existing) card.querySelector('.jgb-product-image').append(element('span', 'jgb-selected-badge', this.labels.selected));
        if (!selected) existing?.remove();
        card.querySelector('button').textContent = selected ? this.labels.edit : this.labels.choose;
      });
    }

    async getProduct(seed, fresh = false, signal) {
      const key = String(seed.id);
      if (!fresh && this.productCache.has(key)) return this.productCache.get(key);
      const product = await this.read(this.productURL(seed.handle, true), 'json', signal);
      if (String(product.id) !== key || Number(product.id) === this.config.placeholderProductId || product.requires_selling_plan || !Array.isArray(product.variants)) throw new Error('Unsupported product');
      product.variants = product.variants.filter(variant => Number.isSafeInteger(Number(variant.id)) && !variant.requires_selling_plan && Array.isArray(variant.options));
      if (!product.variants.length) throw new Error('No variants');
      product.options = (product.options || []).map((option, index) => ({
        name: typeof option === 'string' ? option : option.name,
        values: [...new Set(product.variants.map(variant => variant.options[index]))]
      }));
      product.discountCollectionIds = seed.discountCollectionIds || this.catalog.get(key)?.discountCollectionIds || [];
      this.productCache.set(key, product);
      return product;
    }

    async openProduct(id, source) {
      const selected = this.selected.get(id);
      const seed = selected?.product || this.catalog.get(id);
      if (!seed) return;
      this.setStep(2);
      const sequence = ++this.modalSequence;
      this.modalController?.abort();
      this.modalController = new AbortController();
      this.returnId = id;
      this.returnSource = source;
      this.returnControl = document.activeElement;
      this.modalProduct = null;
      this.modalVariant = null;
      this.querySelector('[data-dialog-title]').textContent = seed.title;
      this.querySelector('[data-dialog-status]').classList.remove('is-error');
      this.querySelector('[data-dialog-status]').textContent = this.labels.loading;
      this.querySelector('[data-options]').replaceChildren();
      this.querySelector('[data-dialog-image]').replaceChildren();
      this.querySelector('[data-dialog-price]').textContent = '';
      this.querySelector('[data-confirm-selection]').disabled = true;
      this.querySelector('[data-product-link]').href = this.productURL(seed.handle).href;
      if (!this.dialog.open) {
        this.savedOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        this.dialog.showModal();
      }
      try {
        const product = await this.getProduct(seed, true, this.modalController.signal);
        if (sequence !== this.modalSequence || !this.dialog.open) return;
        this.modalProduct = product;
        this.modalVariant = product.variants.find(variant => variant.available && String(variant.id) === String(selected?.variant.id)) || product.variants.find(variant => variant.available) || product.variants[0];
        this.renderOptions();
        this.updateModalVariant();
        this.querySelector('[data-options] [aria-checked="true"], [data-options] select')?.focus({ preventScroll: true });
      } catch {
        if (sequence !== this.modalSequence || this.modalController.signal.aborted) return;
        this.querySelector('[data-dialog-status]').textContent = this.labels.product_error;
        this.querySelector('[data-dialog-status]').classList.add('is-error');
      }
    }

    onDialogClose() {
      ++this.modalSequence;
      this.modalController?.abort();
      if (this.savedOverflow !== undefined) {
        document.documentElement.style.overflow = this.savedOverflow;
        this.savedOverflow = undefined;
      }
      const selector = this.returnSource === 'selection' ? `${this.mobileMedia.matches && !this.setDialog.open ? '[data-mobile-prints] ' : ''}[data-edit="${this.returnId}"]` : `button[data-choose="${this.returnId}"]`;
      const returnControl = this.returnControl?.isConnected ? this.returnControl : this.querySelector(selector);
      (returnControl || this.querySelector('[data-catalog-heading]'))?.focus({ preventScroll: true });
      this.setStep(this.setDialog.open ? 3 : 1);
    }

    renderOptions() {
      const container = this.querySelector('[data-options]');
      container.replaceChildren();
      const product = this.modalProduct;
      if (product.variants.length === 1 && product.variants[0].title === 'Default Title') return;
      product.options.forEach((option, index) => {
        if (/^(finish|finish type|finishing|material|frame|ausführung|materiale|finitura|finition|acabado|malzeme|çerçeve)$/i.test(option.name)) {
          const field = element('fieldset', 'jgb-option jgb-finish-option');
          const legend = element('legend', '', option.name);
          const group = element('div', 'jgb-finish-choices');
          group.setAttribute('role', 'radiogroup');
          group.setAttribute('aria-label', option.name);
          option.values.forEach(value => {
            const choice = element('button', 'jgb-finish-choice');
            choice.type = 'button';
            choice.dataset.finishIndex = String(index);
            choice.dataset.finishValue = value;
            choice.setAttribute('role', 'radio');
            const visual = element('span', 'jgb-finish-image');
            const key = this.finishImageKey(value);
            const image = imageURL(this.config.finishImages?.[key]);
            if (image) visual.append(picture(image, ''));
            else visual.append(element('span', 'jgb-finish-fallback'));
            choice.append(visual, element('span', 'jgb-finish-label', value));
            group.append(choice);
          });
          field.append(legend, group);
          container.append(field);
          return;
        }
        const field = element('div', 'jgb-option');
        const label = element('label', '', option.name);
        const select = element('select');
        select.id = `GalleryOption-${this.dataset.sectionId}-${index}`;
        select.dataset.optionIndex = String(index);
        label.htmlFor = select.id;
        option.values.forEach(value => {
          const choice = element('option', '', value);
          choice.value = value;
          select.append(choice);
        });
        field.append(label, select);
        container.append(field);
      });
    }

    finishImageKey(value) {
      if (/canvas|leinwand|kanvas/i.test(value)) return 'canvas';
      if (/black|schwarz|siyah/i.test(value)) return 'black';
      if (/white|weiß|weiss|beyaz/i.test(value)) return 'white';
      if (/oak|eiche|meşe/i.test(value)) return 'oak';
      if (/poster|unframed|no frame|rahmenlos/i.test(value)) return 'poster';
      return '';
    }

    changeOption(index, value) {
      if (!this.modalProduct || this.busy) return;
      const desired = [...this.modalVariant.options];
      desired[index] = value;
      const matches = variant => desired.slice(0, index + 1).every((entry, position) => variant.options[position] === entry);
      this.modalVariant = this.modalProduct.variants.find(variant => variant.available && desired.every((entry, position) => variant.options[position] === entry)) || this.modalProduct.variants.find(variant => variant.available && matches(variant)) || this.modalProduct.variants.find(matches) || this.modalVariant;
      this.updateModalVariant();
    }

    updateModalVariant() {
      const product = this.modalProduct;
      const variant = this.modalVariant;
      this.querySelectorAll('[data-option-index]').forEach(select => {
        const index = Number(select.dataset.optionIndex);
        for (const option of select.options) {
          option.disabled = !product.variants.some(candidate => candidate.available && candidate.options[index] === option.value && variant.options.slice(0, index).every((value, position) => candidate.options[position] === value));
        }
        select.value = variant.options[index];
      });
      this.querySelectorAll('[data-finish-value]').forEach(choice => {
        const index = Number(choice.dataset.finishIndex);
        const checked = choice.dataset.finishValue === variant.options[index];
        choice.disabled = !product.variants.some(candidate => candidate.available && candidate.options[index] === choice.dataset.finishValue && variant.options.slice(0, index).every((value, position) => candidate.options[position] === value));
        choice.setAttribute('aria-checked', String(checked));
        choice.tabIndex = checked ? 0 : -1;
      });
      const image = imageFor(product, variant);
      const container = this.querySelector('[data-dialog-image]');
      container.replaceChildren();
      if (image) container.append(picture(image, product.title));
      this.querySelector('[data-dialog-price]').textContent = this.money(variant.price);
      const status = this.querySelector('[data-dialog-status]');
      status.textContent = variant.available ? '' : this.labels.sold_out;
      status.classList.toggle('is-error', !variant.available);
      const button = this.querySelector('[data-confirm-selection]');
      button.disabled = !variant.available;
      button.textContent = this.selected.has(String(product.id)) ? this.labels.update_print : this.labels.add_print;
      const url = this.productURL(product.handle);
      url.searchParams.set('variant', String(variant.id));
      this.querySelector('[data-product-link]').href = url.href;
    }

    confirmSelection() {
      if (this.busy || !this.modalProduct || !this.modalVariant?.available) return;
      this.selected.set(String(this.modalProduct.id), { product: this.modalProduct, variant: this.modalVariant, unavailable: false });
      this.renderSummary();
      this.updateCatalogSelection();
      this.setMessage(this.labels.selection_added);
      this.dialog.close();
    }

    renderSummary() {
      const list = this.querySelector('[data-selected]');
      const wall = this.querySelector('[data-wall-preview]');
      const mobilePrints = this.querySelector('[data-mobile-prints]');
      list.replaceChildren();
      wall.replaceChildren();
      mobilePrints.replaceChildren();
      let total = 0;
      this.selected.forEach(({ product, variant, unavailable }, id) => {
        total += Number(variant.price);
        const item = element('li', `jgb-selection${unavailable ? ' is-unavailable' : ''}`);
        const image = imageFor(product, variant);
        const thumbnailItem = element('li');
        const thumbnail = element('button', 'jgb-mobile-print');
        thumbnail.type = 'button';
        thumbnail.dataset.edit = id;
        thumbnail.setAttribute('aria-haspopup', 'dialog');
        thumbnail.setAttribute('aria-label', `${this.labels.edit}: ${product.title}, ${variant.options.join(' / ')}`);
        thumbnail.title = `${product.title} · ${variant.options.join(' / ')}`;
        if (image) thumbnail.append(picture(image, ''));
        else thumbnail.append(element('span', '', product.title));
        const thumbnailNumber = element('span', 'jgb-mobile-print-number', String(mobilePrints.children.length + 1));
        thumbnailNumber.setAttribute('aria-hidden', 'true');
        thumbnail.append(thumbnailNumber);
        thumbnailItem.append(thumbnail);
        mobilePrints.append(thumbnailItem);
        if (image) {
          item.append(picture(image, ''));
          wall.append(picture(image, ''));
        }
        const details = element('div');
        details.append(element('h3', '', product.title));
        if (variant.title !== 'Default Title') details.append(element('p', '', variant.options.join(' / ')));
        if (unavailable) details.append(element('p', '', this.labels.sold_out));
        details.append(element('strong', '', this.money(variant.price)));
        const actions = element('div', 'jgb-selection-actions');
        for (const action of ['edit', 'remove']) {
          const button = element('button', '', this.labels[action]);
          button.type = 'button';
          button.dataset[action] = id;
          button.setAttribute('aria-label', `${this.labels[action]}: ${product.title}`);
          actions.append(button);
        }
        details.append(actions);
        item.append(details);
        list.append(item);
      });
      for (let slot = this.selected.size; slot < 3; slot += 1) wall.append(element('span', 'jgb-empty-slot', '+'));
      this.querySelector('[data-empty-selection]').hidden = this.selected.size > 0;
      mobilePrints.hidden = this.selected.size === 0;
      this.querySelector('[data-mobile-tray-heading]').hidden = this.selected.size === 0;
      this.querySelector('[data-mobile-selection-count]').textContent = this.selectionLabel(this.selected.size);
      this.classList.toggle('has-selected-prints', this.selected.size > 0);
      this.querySelector('[data-selection-count]').textContent = String(this.selected.size);
      this.querySelector('[data-selection-count]').setAttribute('aria-label', this.selectionLabel(this.selected.size));
      const reward = this.rewardState();
      this.renderRewards(reward);
      const estimate = Math.max(0, total - reward.savings);
      this.querySelector('[data-subtotal-row]').hidden = reward.savings === 0;
      this.querySelector('[data-subtotal]').textContent = this.money(total);
      this.querySelector('[data-savings-row]').hidden = reward.savings === 0;
      this.querySelector('[data-savings]').textContent = `−${this.money(reward.savings)}`;
      this.querySelector('[data-total]').textContent = this.money(estimate);
      this.querySelector('.jgb-total > span').textContent = reward.savings ? this.labels.estimated_total : this.labels.total;
      this.querySelector('[data-total-note]').textContent = reward.tiers.length ? this.labels.savings_note : this.labels.total_note;
      this.querySelector('[data-mobile-total]').textContent = `${this.selected.size} · ${this.money(estimate)}`;
      this.querySelector('[data-mobile-savings]').textContent = reward.message || this.labels.set_empty_short;
      const mobileButton = this.querySelector('[data-show-selection]');
      mobileButton.setAttribute('aria-label', `${this.labels.view_set}: ${this.selectionLabel(this.selected.size)}, ${this.money(estimate)}`);
      this.addButton.disabled = this.busy || !this.selected.size || [...this.selected.values()].some(item => item.unavailable) || Boolean(this.uncertain);
      this.addButton.textContent = this.busy ? this.labels.adding_set : this.labels.add_set;
    }

    rewardState(now = Date.now()) {
      const selections = [...this.selected.values()].filter(item => !item.unavailable);
      const tiers = (this.config.discountTiers || []).filter(tier =>
        Number(tier.quantity) > 0 && Number(tier.percent) > 0 && Number(tier.percent) <= 100 &&
        (!tier.startsAt || Date.parse(tier.startsAt) <= now) && (!tier.endsAt || Date.parse(tier.endsAt) > now)
      ).sort((a, b) => a.quantity - b.quantity);
      const evaluated = tiers.map(tier => {
        const eligible = selections.filter(({ product }) => tier.all === true ||
          (tier.productIds || []).map(String).includes(String(product.id)) ||
          (product.discountCollectionIds || []).some(id => (tier.collectionIds || []).map(String).includes(String(id))));
        const reached = eligible.length >= Number(tier.quantity);
        const savings = reached ? eligible.reduce((sum, item) => sum + Math.floor(Number(item.variant.price) * Number(tier.percent) / 100), 0) : 0;
        return { ...tier, eligible: eligible.length, reached, savings };
      });
      const best = evaluated.reduce((found, tier) => tier.savings > (found?.savings || 0) ? tier : found, null);
      const next = evaluated.find(tier => !tier.reached && (!best || tier.percent > best.percent));
      const message = best ? this.label('savings_amount', { amount: this.money(best.savings) }) : next?.quantity === 1 ? this.label('savings_offer', { percent: next.percent }) : next ? this.label('savings_progress', { count: next.quantity - next.eligible, percent: next.percent, plural: next.quantity - next.eligible === 1 ? '' : 's' }) : '';
      return { tiers: evaluated, best, next, savings: best?.savings || 0, message };
    }

    renderRewards(state) {
      const section = this.querySelector('[data-rewards]');
      section.hidden = !state.tiers.length;
      this.querySelector('[data-reward-message]').textContent = state.message;
      const container = this.querySelector('[data-reward-tiers]');
      container.replaceChildren();
      container.hidden = state.tiers.length === 1 && state.tiers[0].quantity === 1;
      state.tiers.forEach(tier => {
        const item = element('div', `jgb-reward-tier${tier.reached ? ' is-reached' : ''}`);
        const track = element('span', 'jgb-reward-track');
        const fill = element('span');
        fill.style.width = `${Math.min(100, tier.eligible / tier.quantity * 100)}%`;
        track.append(fill);
        const caption = element('span', 'jgb-reward-caption');
        caption.append(element('span', '', this.label('savings_tier', { count: tier.quantity })), element('strong', '', `−${tier.percent}%`));
        item.append(track, caption);
        container.append(item);
      });
    }

    setMessage(text, error = false) {
      const message = this.querySelector('[data-set-message]');
      message.textContent = text;
      message.classList.toggle('is-error', error);
    }

    async addSet() {
      if (this.busy || this.uncertain || !this.selected.size || [...this.selected.values()].some(item => item.unavailable)) return;
      this.busy = true;
      const disabledState = new Map();
      this.querySelectorAll('button,input,select').forEach(control => { disabledState.set(control, control.disabled); control.disabled = true; });
      this.addButton.textContent = this.labels.adding_set;
      this.setMessage('');
      this.setAttribute('aria-busy', 'true');
      let mutationStarted = false;
      let confirmed = false;
      try {
        const selected = [...this.selected.entries()];
        const refreshed = await Promise.all(selected.map(async ([id, item]) => ({ id, item, product: await this.getProduct(item.product, true) })));
        let pricesChanged = false;
        let availabilityChanged = false;
        refreshed.forEach(({ id, item, product }) => {
          const variant = product.variants.find(candidate => String(candidate.id) === String(item.variant.id));
          if (!variant?.available) {
            this.selected.set(id, { product, variant: variant || item.variant, unavailable: true });
            availabilityChanged = true;
          } else {
            if (Number(variant.price) !== Number(item.variant.price)) pricesChanged = true;
            this.selected.set(id, { product, variant, unavailable: false });
          }
        });
        if (availabilityChanged || pricesChanged) {
          this.setMessage(availabilityChanged ? this.labels.availability_changed : this.labels.prices_changed, true);
          return;
        }
        const drawer = document.querySelector('cart-drawer');
        const sections = drawer?.getSectionsToRender?.().map(section => section.id) || ['cart-drawer', 'cart-icon-bubble'];
        const payload = {
          items: [...this.selected.values()].map(item => ({ id: Number(item.variant.id), quantity: 1 })),
          sections: sections.slice(0, 5), sections_url: location.pathname
        };
        const cartBefore = await this.read(`${this.root}cart.js`);
        mutationStarted = true;
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 25000);
        let response;
        let result;
        try {
          response = await fetch(`${this.root}cart/add.js`, {
            method: 'POST', credentials: 'same-origin', signal: controller.signal,
            headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify(payload)
          });
          result = await response.json();
        } finally { clearTimeout(timeout); }
        if (!response.ok || result.status) {
          this.querySelector('[data-view-bag]').hidden = false;
          try {
            const cartAfter = await this.read(`${this.root}cart.js`);
            const quantity = (cart, id) => (cart.items || []).filter(item => String(item.variant_id) === String(id)).reduce((sum, item) => sum + Number(item.quantity), 0);
            this.selected.forEach((item, id) => {
              if (quantity(cartAfter, item.variant.id) > quantity(cartBefore, item.variant.id)) this.selected.delete(id);
            });
            this.updateCatalogSelection();
            document.dispatchEvent(new CustomEvent('cart:change', { detail: { cart: cartAfter, sectionId: this.dataset.sectionId } }));
            if (!this.selected.size) {
              confirmed = true;
              this.setMessage(this.labels.added_set);
              await this.showCart({}, drawer);
            } else this.setMessage(typeof result.description === 'string' ? result.description : this.labels.add_error, true);
          } catch {
            this.uncertain = true;
            this.setMessage(this.labels.uncertain_error, true);
          }
          return;
        }
        confirmed = true;
        this.selected.clear();
        this.renderSummary();
        this.updateCatalogSelection();
        this.setMessage(this.labels.added_set);
        this.querySelector('[data-view-bag]').hidden = false;
        await this.showCart(result, drawer);
      } catch {
        if (confirmed) {
          this.setMessage(this.labels.added_set);
          this.querySelector('[data-view-bag]').hidden = false;
        } else if (mutationStarted) {
          this.uncertain = true;
          this.setMessage(this.labels.uncertain_error, true);
          this.querySelector('[data-view-bag]').hidden = false;
        } else this.setMessage(this.labels.add_error, true);
      } finally {
        this.busy = false;
        this.removeAttribute('aria-busy');
        disabledState.forEach((disabled, control) => { if (control.isConnected) control.disabled = disabled; });
        this.renderSummary();
      }
    }

    async showCart(result, drawer) {
      if (this.setDialog?.open) this.setDialog.close();
      let sections = result.sections;
      const validDrawer = html => typeof html === 'string' && new DOMParser().parseFromString(html, 'text/html').querySelector('#CartDrawer .drawer__inner');
      if (drawer?.renderContents && !validDrawer(sections?.['cart-drawer'])) {
        const url = this.localURL(location.pathname);
        url.searchParams.set('sections', 'cart-drawer,cart-icon-bubble');
        sections = await this.read(url);
      }
      this.read(`${this.root}cart.js`).then(cart => {
        document.dispatchEvent(new CustomEvent('cart:change', { detail: { cart, sectionId: this.dataset.sectionId } }));
      }).catch(() => {});
      if (drawer?.renderContents && validDrawer(sections?.['cart-drawer'])) {
        drawer.classList.remove('is-empty');
        drawer.setActiveElement?.(this.querySelector('[data-catalog-heading]'));
        drawer.renderContents({ ...result, sections });
      } else {
        const url = this.localURL(this.config.cartUrl);
        url.searchParams.set('view', 'page');
        location.assign(url.href);
      }
    }
  }

  customElements.define('joffz-gallery-builder', GalleryBuilder);
})();
